import { Component, Injector, OnInit, OnDestroy, computed, effect, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Firestore, collection, collectionData, doc, getDoc, query, where } from '@angular/fire/firestore';
import { combineLatest, Observable, Subject, Subscription, of } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { ApprovalService } from '../../services/approval.service';
import { UserService } from '../../services/user.service';
import { UserProfileDisplayService } from '../../services/user-profile-display.service';
import { FirestoreProjectService } from '../../services/firestore-project.service';
import { DepartmentSchemesService, DepartmentEntry } from '../../services/department-schemes.service';
import { DepartmentMatchingService } from '../../services/department-matching.service';
import { PhotoUploadService } from '../../services/photo-upload.service';
import { ApprovalRequest, ApprovalHistory } from '../../models/approval.model';
import { CompareChangesComponent } from '../../components/shared/compare-changes/compare-changes.component';
import { UserRole } from '../../models/user.model';
import { DEFAULT_OPERATOR_LOCATION_BLOCK, NAC_BLOCKS, GP_BY_BLOCK } from '../../constants/operator-master-data';
import { LocationMasterService } from '../../services/location-master.service';
import {
  formatBlockAccessLabel,
  projectMatchesBlockAccess,
  userHasUnrestrictedBlockAccess,
} from '../../utils/block-access.utils';
import { MasterLocation } from '../../models/location.model';

@Component({
  selector: 'app-approver-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, CompareChangesComponent],
  templateUrl: './approver-dashboard.component.html',
  styleUrls: ['./approver-dashboard.component.scss']
})
export class ApproverDashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private injector = inject(Injector);
  private authService = inject(AuthService);
  private router = inject(Router);
  private approvalService = inject(ApprovalService);
  private userService = inject(UserService);
  readonly profileUi = inject(UserProfileDisplayService);
  private projectService = inject(FirestoreProjectService);
  private deptService = inject(DepartmentSchemesService);
  private deptMatching = inject(DepartmentMatchingService);
  private firestore = inject(Firestore);
  private photoUploadService = inject(PhotoUploadService);
  private locationMaster = inject(LocationMasterService);
  private departmentAssignmentSub: Subscription | null = null;
  private liveProjectsSub: Subscription | null = null;
  private approverRequestsSub: Subscription | null = null;

  masterLocations = signal<MasterLocation[]>([]);

  /** Block options in edit form — scoped to approver assignment. */
  locationBlocks = computed(() => {
    const user = this.currentUser();
    void this.locationMaster.locations();
    return this.locationMaster.formBlockOptions(user?.blockAccess, user?.role);
  });

  currentUser = this.authService.currentUser;
  /** All queue rows routed to this approver (pending + approved + rejected). */
  rawRequests = signal<ApprovalRequest[]>([]);
  rawHistory = signal<ApprovalHistory[]>([]);
  liveProjects = signal<any[]>([]);
  /** uid → displayName lookup */
  userMap = signal<Map<string, string>>(new Map());
  /** Department IDs (slugs) the approver is assigned to, sourced from /users + /departmentAssignments. */
  mappedDepartmentIds = signal<string[]>([]);
  isLoading = signal<boolean>(false);

  // Compare Modal State (reused from the original approver dashboard)
  isCompareOpen = signal<boolean>(false);
  isReadOnly = signal<boolean>(false);
  selectedRequest = signal<ApprovalRequest | null>(null);
  beforeData = signal<any>(null);
  afterData = signal<any>(null);
  modalMeta = signal<{ operator: string; approver?: string; date?: Date; status?: string; remarks?: string; operation?: string } | null>(null);
  isSubmittingAction = signal<boolean>(false);

  // ── Stat-card popup ───────────────────────────────────────────────
  statModalFilter = signal<'pending' | 'approved' | 'rejected' | null>(null);
  selectStat(filter: 'pending' | 'approved' | 'rejected'): void { this.statModalFilter.set(filter); }
  closeStatModal(): void { this.statModalFilter.set(null); }

  statModalTitle = computed(() => {
    switch (this.statModalFilter()) {
      case 'pending':  return 'Pending Requests';
      case 'approved': return 'Approved Requests';
      case 'rejected': return 'Rejected Requests';
      default:         return '';
    }
  });

  // ── Search / filters / pagination ────────────────────────────────
  projectSearch       = signal('');
  projectStatusFilter = signal('');
  projectSchemeFilter = signal('');
  projectBlockFilter  = signal('');
  projectGpFilter     = signal('');
  opPage     = signal(1);
  opPageSize = signal<number>(25);

  availableFilterSchemes = computed<string[]>(() => {
    const schemes = new Set(
      this.projectRows().map(r => r.scheme).filter((s: any) => s && s !== '—')
    );
    return [...schemes].sort();
  });

  availableFilterBlocks = computed<string[]>(() => {
    const blocks = new Set(
      this.projectRows().map(r => r.block).filter((b: any) => b && b !== '—')
    );
    return [...blocks].sort();
  });

  availableFilterGPs = computed<string[]>(() => {
    const bf = this.projectBlockFilter();
    const gps = new Set(
      this.projectRows()
        .filter(r => !bf || r.block === bf)
        .map((r: any) => r.gp)
        .filter((g: any) => g && g !== '—')
    );
    return [...gps].sort();
  });

  // ── Helpers ─────────────────────────────────────────────────────
  private shortUid(uid?: string): string {
    if (!uid) return '—';
    return uid.length > 10 ? uid.slice(0, 6) + '…' + uid.slice(-4) : uid;
  }

  private toDate(t: any): Date | null {
    if (!t) return null;
    if (t instanceof Date) return t;
    if (typeof t?.toDate === 'function') return t.toDate();
    if (typeof t?.seconds === 'number')   return new Date(t.seconds * 1000);
    if (typeof t === 'number')            return new Date(t);
    if (typeof t === 'string')            return new Date(t);
    return null;
  }

  private tsMs(t: any): number {
    if (!t) return 0;
    if (typeof t?.toDate === 'function') return t.toDate().getTime();
    if (typeof t?.seconds === 'number')   return t.seconds * 1000;
    if (t instanceof Date)                return t.getTime();
    if (typeof t === 'number')            return t;
    if (typeof t === 'string')            return Date.parse(t) || 0;
    return 0;
  }

  /** Normalise queue status across legacy (`status`) and spec (`approvalStatus`) fields. */
  private reqStatus(r: any): 'PENDING' | 'APPROVED' | 'REJECTED' {
    const s = String(r?.status ?? r?.approvalStatus ?? '').toUpperCase();
    if (s === 'PENDING' || s === 'APPROVED' || s === 'REJECTED') return s as any;
    return 'PENDING';
  }

  lastUpdatedOf(row: any): string {
    const ms = this.tsMs(row?.updatedAt);
    if (!ms) return '—';
    return new Date(ms).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  }

  private normalizeDepartmentKey(value: string | null | undefined): string {
    return (value || '')
      .toLowerCase()
      .replace(/[\s&]/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .trim();
  }

  /** Department(s) assigned to the logged-in approver. */
  assignedDepts = computed<DepartmentEntry[]>(() => {
    const user = this.currentUser();
    if (!user) return [];
    const rawIds = [
      ...(user.departmentIds || []),
      ...((user as any).assignedDepartments || []),
      ...(this.mappedDepartmentIds() || []),
    ];
    const allDepts = this.deptService.getDepartments();
    if (rawIds.length > 0) {
      const allowed = new Set(rawIds.map(id => this.normalizeDepartmentKey(id)));
      return allDepts.filter(d => {
        const n  = this.normalizeDepartmentKey(d.name);
        const s  = this.normalizeDepartmentKey(d.shortName);
        return allowed.has(n) || allowed.has(s);
      });
    }
    if (user.role === UserRole.ADMIN) return allDepts;
    return [];
  });

  /** Friendly department label for the header pill. */
  approverDeptDisplay = computed<string>(() => {
    const list = this.assignedDepts();
    if (list.length === 0) return 'No Department Assigned';
    if (list.length === 1) return list[0].name;
    return `${list.length} Departments`;
  });

  approverBlockDisplay = computed(() => {
    const user = this.currentUser();
    return formatBlockAccessLabel(
      user?.blockAccess,
      userHasUnrestrictedBlockAccess(user?.role),
    );
  });

  /** Live projects scoped to assigned department(s) and Block/NAC. */
  approverProjects = computed<any[]>(() => {
    const user = this.currentUser();
    const depts = this.assignedDepts();
    const all = this.liveProjects();
    const locations = this.masterLocations();

    if (user?.role === UserRole.ADMIN) return all;
    if (depts.length === 0) return [];

    const deptFiltered = all.filter(p => {
      for (const d of depts) {
        if (this.deptMatching.projectBelongsToDepartment(p, d, this.deptService.getDepartments())) return true;
      }
      const allowedKeys = new Set(
        depts.flatMap(d => [this.normalizeDepartmentKey(d.name), this.normalizeDepartmentKey(d.shortName)])
      );
      return allowedKeys.has(this.normalizeDepartmentKey(p.dept));
    });

    if (userHasUnrestrictedBlockAccess(user?.role)) return deptFiltered;
    return deptFiltered.filter(p =>
      projectMatchesBlockAccess(p, user?.blockAccess, locations)
    );
  });

  /** True when a queue/history payload belongs to one of this approver's departments. */
  private payloadMatchesApproverDept(payload: Record<string, any> | null | undefined): boolean {
    const user = this.currentUser();
    if (user?.role === UserRole.ADMIN) return true;
    const depts = this.assignedDepts();
    if (depts.length === 0) return false;
    const deptRaw = String(payload?.['dept'] ?? payload?.['department'] ?? '').trim();
    if (!deptRaw) return false;
    const allDeptEntries = this.deptService.getDepartments();
    const resolved = this.deptMatching.resolveDepartment({ dept: deptRaw }, allDeptEntries);
    const deptOk = resolved
      ? depts.some(d => d.name === resolved.name)
      : depts.some(d =>
          this.normalizeDepartmentKey(deptRaw) === this.normalizeDepartmentKey(d.name) ||
          this.normalizeDepartmentKey(deptRaw) === this.normalizeDepartmentKey(d.shortName)
        );
    if (!deptOk) return false;

    if (userHasUnrestrictedBlockAccess(user?.role)) return true;
    return projectMatchesBlockAccess(
      { block: payload?.['block'] ?? payload?.['division'] ?? payload?.['loc'] },
      user?.blockAccess,
      this.masterLocations(),
    );
  }

  private buildProjectRowFromPayload(
    id: string,
    payload: Record<string, any>,
    approvalStatus: 'PENDING' | 'APPROVED' | 'REJECTED',
    pendingRequest: ApprovalRequest | null,
    isPhantom: boolean,
    updatedAt?: any,
  ): any {
    return {
      id,
      projectId:       payload['projectId'] ?? null,
      name:            payload['name'] ?? payload['roadName'] ?? '—',
      scheme:          payload['scheme'] ?? '—',
      gp:              payload['gpName'] ?? payload['gp'] ?? '—',
      district:        payload['district'] ?? payload['loc'] ?? '—',
      block:           payload['block'] ?? payload['division'] ?? payload['loc'] ?? '—',
      lengthKm:        payload['roadLength'] ?? payload['lengthKm'] ?? null,
      costLakh:        payload['cost'] ?? payload['costLakh'] ?? 0,
      spentLakh:       payload['spent'] ?? payload['spentLakh'] ?? 0,
      status:          payload['status'] ?? 'Planned',
      progress:        Number(payload['physical'] ?? payload['progress'] ?? 0),
      updatedAt:       updatedAt ?? payload['updatedAt'] ?? payload['createdAt'] ?? null,
      dept:            payload['dept'] ?? payload['department'],
      hmPriority:            !!payload['hmPriority'],
      recurringIntervention: !!payload['recurringIntervention'],
      approvalStatus,
      pendingRequest,
      isPhantom,
      rawLive:         isPhantom ? null : payload,
    };
  }

  /** Pending requests in this approver's queue. */
  rawPending = computed(() =>
    this.rawRequests().filter(r => this.reqStatus(r) === 'PENDING')
  );

  pendingRequests = computed(() =>
    this.rawPending().map(r => ({
      ...r,
      operator: this.userMap().get(r.operatorUid) || this.shortUid(r.operatorUid),
      submittedOn: this.toDate((r as any).submittedAt ?? r.createdAt),
    }))
  );

  /** History logs (approved + rejected). */
  historyLogs = computed(() =>
    this.rawHistory().map(h => {
      const after  = (h as any).afterData  ?? {};
      const before = (h as any).beforeData ?? {};
      const projectName =
        after?.name || before?.name ||
        (h.targetCollection
          ? `${h.targetCollection}${h.targetDocId ? ' · ' + h.targetDocId.slice(0, 6) : ''}`
          : '—');
      return {
        ...h,
        module:   projectName,
        operator: this.userMap().get(h.operatorUid) || this.shortUid(h.operatorUid),
        approver: h.approverUid && h.approverUid !== 'SYSTEM'
                  ? (this.userMap().get(h.approverUid) || this.shortUid(h.approverUid))
                  : 'System',
        date: this.toDate(h.timestamp),
      };
    })
  );

  /**
   * Unified project-table rows for the single-screen view:
   *   1. Every LIVE project in the approver's dept(s) — with its approval state
   *      derived from the most recent in-flight request (if any).
   *   2. Phantom rows for pending CREATE requests not yet materialised.
   */
  projectRows = computed<any[]>(() => {
    const live     = this.approverProjects();
    const requests = this.rawRequests();
    const history  = this.rawHistory();

    // Most-recent request per live project id
    const byDocId = new Map<string, ApprovalRequest>();
    for (const r of requests) {
      if (r.targetCollection && r.targetCollection !== 'projects') continue;
      const ids = [r.targetDocId, (r as any).createdDocId, (r as any).originalProjectId]
        .filter(Boolean) as string[];
      for (const id of ids) {
        const prev = byDocId.get(id);
        if (!prev || this.tsMs((r as any).createdAt ?? (r as any).submittedAt) > this.tsMs((prev as any).createdAt ?? (prev as any).submittedAt)) {
          byDocId.set(id, r);
        }
      }
    }

    const rows: any[] = live.map(p => {
      const req = byDocId.get(p.id);
      const reqStatus = req ? this.reqStatus(req) : null;
      const approvalStatus = reqStatus === 'PENDING' ? 'PENDING' : 'APPROVED';
      return {
        id:              p.id,
        projectId:       (p as any).projectId ?? null,
        name:            p.name ?? p.roadName ?? '—',
        scheme:          p.scheme ?? '—',
        gp:              (p as any).gpName ?? p.gp ?? '—',
        district:        p.district ?? p.loc ?? '—',
        block:           p.block ?? p.division ?? p.loc ?? '—',
        lengthKm:        p.roadLength ?? p.lengthKm ?? null,
        costLakh:        p.cost ?? p.costLakh ?? 0,
        spentLakh:       p.spent ?? p.spentLakh ?? 0,
        status:          p.status ?? 'Planned',
        progress:        Number(p.physical ?? p.progress ?? 0),
        updatedAt:       p.updatedAt ?? p.createdAt ?? null,
        dept:            p.dept,
        hmPriority:            !!p.hmPriority,
        recurringIntervention: !!p.recurringIntervention,
        approvalStatus,
        pendingRequest:  req && this.reqStatus(req) === 'PENDING' ? req : null,
        isPhantom:       false,
        rawLive:         p,
      };
    });

    const rowIds = new Set(rows.map(r => r.id));

    // Pending CREATE phantoms — not yet materialised in Firestore.
    for (const r of requests) {
      if (r.targetCollection !== 'projects' || r.operation !== 'create') continue;
      const st = this.reqStatus(r);
      if (st !== 'PENDING') continue;
      const docId = r.targetDocId || (r as any).createdDocId;
      if (docId && rowIds.has(docId)) continue;
      const payload = (r as any).newData ?? r.payload ?? {};
      if (!this.payloadMatchesApproverDept(payload)) continue;
      const rowId = docId ?? `req-${r.id}`;
      if (rowIds.has(rowId)) continue;
      rows.push(this.buildProjectRowFromPayload(
        rowId,
        payload,
        'PENDING',
        r,
        true,
        (r as any).submittedAt ?? r.createdAt,
      ));
      rowIds.add(rowId);
    }

    // Recently APPROVED CREATE rows — bridge until the live projects stream catches up.
    for (const r of requests) {
      if (r.targetCollection !== 'projects' || r.operation !== 'create') continue;
      if (this.reqStatus(r) !== 'APPROVED') continue;
      const docId = r.targetDocId || (r as any).createdDocId;
      if (!docId || rowIds.has(docId)) continue;
      const payload = (r as any).approvedPayload ?? (r as any).newData ?? r.payload ?? {};
      if (!this.payloadMatchesApproverDept(payload)) continue;
      rows.push(this.buildProjectRowFromPayload(
        docId,
        payload,
        'APPROVED',
        null,
        true,
        (r as any).approvedAt ?? r.updatedAt ?? r.createdAt,
      ));
      rowIds.add(docId);
    }

    // History fallback for approved creates missing from live (stream lag / refresh).
    for (const h of history) {
      if (h.status !== 'APPROVED' || h.operation !== 'create' || h.targetCollection !== 'projects') continue;
      const docId = h.targetDocId;
      if (!docId || rowIds.has(docId)) continue;
      const payload = (h.afterData ?? {}) as Record<string, any>;
      if (!this.payloadMatchesApproverDept(payload)) continue;
      rows.push(this.buildProjectRowFromPayload(
        docId,
        payload,
        'APPROVED',
        null,
        true,
        h.timestamp,
      ));
      rowIds.add(docId);
    }

    return rows;
  });

  /**
   * Pending scheme-only creation requests (submitted via "Submit Scheme for
   * Approval" with no project attached — see operator dashboard). These carry
   * targetCollection: 'schemes' and are deliberately excluded from
   * projectRows()/statModalRows(), which are project-shaped, so they need
   * their own list + approve/reject surface. Reuses approveRow()/rejectRow(),
   * which only need `row.pendingRequest` and `row.name`.
   */
  pendingSchemeRequests = computed<Array<{
    id: string; name: string; dept: string; totalCost: number; spent: number;
    operator: string; submittedOn: Date | null; pendingRequest: ApprovalRequest;
  }>>(() => {
    const user = this.currentUser();
    const isAdmin = user?.role === UserRole.ADMIN;
    const depts = this.assignedDepts();
    const allDeptEntries = this.deptService.getDepartments();

    return this.rawPending()
      .filter(r => r.targetCollection === 'schemes' && r.operation === 'create')
      .filter(r => {
        if (isAdmin) return true;
        if (depts.length === 0) return false;
        const payload = (r as any).newData ?? r.payload ?? {};
        const deptRaw = String(payload?.dept ?? payload?.department ?? '').trim();
        if (!deptRaw) return false;
        const resolved = this.deptMatching.resolveDepartment({ dept: deptRaw }, allDeptEntries);
        return resolved
          ? depts.some(d => d.name === resolved.name)
          : depts.some(d =>
              this.normalizeDepartmentKey(deptRaw) === this.normalizeDepartmentKey(d.name) ||
              this.normalizeDepartmentKey(deptRaw) === this.normalizeDepartmentKey(d.shortName)
            );
      })
      .map(r => {
        const payload = (r as any).newData ?? r.payload ?? {};
        return {
          id:          r.id!,
          name:        payload.name ?? '(unnamed scheme)',
          dept:        payload.dept ?? '—',
          totalCost:   Number(payload.totalCost) || 0,
          spent:       Number(payload.spent) || 0,
          operator:    this.userMap().get(r.operatorUid) || this.shortUid(r.operatorUid),
          submittedOn: this.toDate((r as any).submittedAt ?? r.createdAt),
          pendingRequest: r,
        };
      });
  });

  pendingSchemeRequestsCount = computed(() => this.pendingSchemeRequests().length);

  // ── Stat counters ────────────────────────────────────────────────
  pendingProjectsCount  = computed(() => this.projectRows().filter(r => r.approvalStatus === 'PENDING').length);
  approvedProjectsCount = computed(() => this.projectRows().filter(r => r.approvalStatus === 'APPROVED').length);
  rejectedProjectsCount = computed(() => this.projectRows().filter(r => r.approvalStatus === 'REJECTED').length);
  /** Rows eligible for the main table (excludes pending — those live in the Pending popup). */
  tableBaseRowsCount = computed(() => this.projectRows().filter(r => r.approvalStatus !== 'PENDING' && !r.isPhantom).length);

  /** Rows for the stat-card popup. */
  statModalRows = computed<any[]>(() => {
    const filter = this.statModalFilter();
    if (!filter) return [];
    if (filter === 'pending') {
      return this.projectRows().filter(r => r.approvalStatus === 'PENDING');
    }
    // Approved / Rejected — pull from history logs and map to a uniform row shape
    const target = filter === 'approved' ? 'APPROVED' : 'REJECTED';
    return this.historyLogs()
      .filter(h => h.status === target)
      .map((h: any) => {
        const data = h.afterData ?? h.beforeData ?? {};
        return {
          id:              h.targetDocId ?? h.id ?? h.requestId,
          projectId:       data.projectId ?? null,
          name:            h.module || data.name || '—',
          scheme:          data.scheme ?? '—',
          district:        data.district ?? data.loc ?? '—',
          block:           data.block ?? data.division ?? data.loc ?? '—',
          lengthKm:        data.roadLength ?? data.lengthKm ?? null,
          costLakh:        data.cost ?? data.costLakh ?? 0,
          status:          data.status ?? 'Planned',
          progress:        Number(data.physical ?? 0),
          updatedAt:       h.timestamp,
          approvalStatus:  target,
          operatorName:    h.operator,
          approverName:    h.approver,
          remarks:         h.remarks,
          historyRow:      h,                            // for view → history compare modal
        };
      });
  });

  // ── Filtered + paginated view of the main project table ─────────
  /** Pending rows live only in the Pending stat popup — not the main table. */
  filteredProjectRows = computed<any[]>(() => {
    const q      = this.projectSearch().trim().toLowerCase();
    const sfilt  = this.projectStatusFilter();
    const scheme = this.projectSchemeFilter();
    const block  = this.projectBlockFilter();
    const gp     = this.projectGpFilter();

    return this.projectRows().filter(row => {
      if (row.approvalStatus === 'PENDING') return false;
      // Main table shows only live Firestore projects — not history/queue phantoms.
      if (row.isPhantom) return false;

      if (sfilt  && (row.status ?? 'Planned') !== sfilt) return false;
      if (scheme && row.scheme !== scheme)                return false;
      if (block  && row.block  !== block)                 return false;
      if (gp     && row.gp     !== gp)                   return false;
      if (q) {
        const hay = [
          row.name, row.scheme, row.gp, row.block,
          row.projectId, row.id,
        ].filter(v => v !== undefined && v !== null)
         .map(v => String(v).toLowerCase()).join(' ');
        if (!hay.includes(q)) return false;
      }
      return true;
    }).sort((a: any, b: any) => this.tsMs(b.updatedAt) - this.tsMs(a.updatedAt));
  });

  get opTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredProjectRows().length / this.opPageSize()));
  }
  get opPagedProjects(): any[] {
    const list = this.filteredProjectRows();
    const max  = Math.max(1, Math.ceil(list.length / this.opPageSize()));
    if (this.opPage() > max) queueMicrotask(() => this.opPage.set(max));
    const safePage = Math.min(this.opPage(), max);
    const start    = (safePage - 1) * this.opPageSize();
    return list.slice(start, start + this.opPageSize());
  }
  opSlForIndex(i: number): number {
    return (this.opPage() - 1) * this.opPageSize() + i + 1;
  }
  opGoToPage(p: number): void {
    const max = this.opTotalPages;
    this.opPage.set(Math.max(1, Math.min(p, max)));
  }
  opPrevPage(): void { this.opGoToPage(this.opPage() - 1); }
  opNextPage(): void { this.opGoToPage(this.opPage() + 1); }
  opSetPageSize(size: number | string): void {
    const n = Number(size) || 25;
    this.opPageSize.set(n);
    this.opPage.set(1);
  }
  get opPageRange(): string {
    const total = this.filteredProjectRows().length;
    if (total === 0) return '0 of 0';
    const start = (this.opPage() - 1) * this.opPageSize() + 1;
    const end   = Math.min(start + this.opPageSize() - 1, total);
    return `${start}–${end} of ${total}`;
  }
  opResetTableFilters(): void {
    this.projectSearch.set('');
    this.projectStatusFilter.set('');
    this.projectSchemeFilter.set('');
    this.projectBlockFilter.set('');
    this.projectGpFilter.set('');
    this.opPage.set(1);
  }

  /** Pill helpers — same convention as the operator dashboard. */
  projectStatusClass(status: string | undefined): string {
    switch (status) {
      case 'Completed':   return 'op-pill op-completed';
      case 'In Progress': return 'op-pill op-inprogress';
      case 'Stuck':       return 'op-pill op-stuck';
      case 'Planned':     return 'op-pill op-planned';
      default:            return 'op-pill op-planned';
    }
  }
  approvalPillClass(status: string): string {
    switch (status) {
      case 'PENDING':  return 'op-pill op-pending';
      case 'APPROVED': return 'op-pill op-approved';
      case 'REJECTED': return 'op-pill op-rejected';
      default:         return 'op-pill';
    }
  }

  // ─── Edit Modal State ────────────────────────────────────────────
  isEditModalOpen      = signal(false);
  isEditSubmitting     = signal(false);
  private editingLiveProjectId: string | null = null;
  private editingBaseline: Record<string, any> | null = null;
  private editingPendingRequestId: string | null = null;

  // ── Pending-edit mode flag (reuses the edit modal, submits as approval) ──
  pendingEditMode = signal(false);

  // ── Pending Project View Modal ───────────────────────────────────────────
  isPendingViewOpen    = signal(false);
  pendingViewData      = signal<Record<string, any> | null>(null);
  pendingViewRequest   = signal<ApprovalRequest | null>(null);
  pendingViewRow       = signal<any>(null);
  pendingViewLightbox  = signal<string | null>(null);
  isRejectDialogOpen   = signal(false);
  rejectDialogRemarks  = signal('');
  isSubmittingFromView = signal(false);

  pendingViewPhotos = computed(() => {
    const photos = (this.pendingViewData()?.['projectPhotos'] ?? {}) as any;
    return {
      before: (photos['before'] ?? []) as string[],
      during: (photos['during'] ?? []) as string[],
      after:  (photos['after']  ?? []) as string[],
    };
  });

  pendingViewDates = computed(() => {
    const dates = (this.pendingViewData()?.['photoDates'] ?? {}) as any;
    return {
      before: (dates['before'] ?? null) as string | null,
      during: (dates['during'] ?? null) as string | null,
      after:  (dates['after']  ?? null) as string | null,
    };
  });

  pendingViewSubmittedDate = computed(() => {
    const req = this.pendingViewRequest();
    return req ? this.toDate((req as any).submittedAt ?? req.createdAt) : null;
  });

  pendingViewOperatorName = computed(() => {
    const req = this.pendingViewRequest();
    return req ? (this.userMap().get(req.operatorUid) || this.shortUid(req.operatorUid)) : '—';
  });

  editFormData = signal<{
    deptName: string; projectName: string; schemeName: string;
    block: string; gp: string; status: string; costCr: number | null;
    spentCr: number; yearOfCreation: string; roadLength: number | null;
    executantName: string; executantContact: string; editRemarks: string;
    photoDateBefore: string; photoDateDuring: string; photoDateAfter: string;
  }>({
    deptName: '', projectName: '', schemeName: '',
    block: DEFAULT_OPERATOR_LOCATION_BLOCK, gp: '',
    status: 'Planned', costCr: null, spentCr: 0,
    yearOfCreation: '', roadLength: null,
    executantName: '', executantContact: '', editRemarks: '',
    photoDateBefore: '', photoDateDuring: '', photoDateAfter: '',
  });

  patchEditForm(patch: Partial<{
    deptName: string; projectName: string; schemeName: string;
    block: string; gp: string; status: string; costCr: number | null;
    spentCr: number; yearOfCreation: string; roadLength: number | null;
    executantName: string; executantContact: string; editRemarks: string;
    photoDateBefore: string; photoDateDuring: string; photoDateAfter: string;
  }>): void {
    this.editFormData.update(f => ({ ...f, ...patch }));
  }

  editExtraSchemeName = signal<string | null>(null);

  editDeptSchemes = computed<string[]>(() => {
    const deptName = this.editFormData().deptName.trim();
    if (!deptName) return [];
    const dept = this.deptService.getDepartments().find(d =>
      this.normalizeDepartmentKey(d.name) === this.normalizeDepartmentKey(deptName)
    );
    const schemes = dept?.schemes?.map(s => s.name) ?? [];
    const extra = this.editExtraSchemeName();
    if (extra && !schemes.some(s => s.toLowerCase() === extra.toLowerCase())) {
      return [extra, ...schemes];
    }
    return schemes;
  });

  // Block → GP reactivity
  editSelectedBlock  = signal<string>(DEFAULT_OPERATOR_LOCATION_BLOCK);
  editGpOptions      = computed<string[]>(() => GP_BY_BLOCK[this.editSelectedBlock()] ?? []);
  editIsNacBlock     = computed(() => NAC_BLOCKS.has(this.editSelectedBlock()));
  /** When editing, the live project's year may fall outside the dropdown's normal range. */
  editExtraYear = signal<number | null>(null);
  editYearOptions    = computed<number[]>(() => {
    const years: number[] = [];
    for (let y = 2026; y >= 2020; y--) years.push(y);
    const extra = this.editExtraYear();
    if (extra && !years.includes(extra)) years.unshift(extra);
    return years;
  });

  // Photo state (Before / During / After)
  editBeforeFiles     = signal<File[]>([]);
  editDuringFiles     = signal<File[]>([]);
  editAfterFiles      = signal<File[]>([]);
  editBeforeDataUrls  = signal<string[]>([]);
  editDuringDataUrls  = signal<string[]>([]);
  editAfterDataUrls   = signal<string[]>([]);
  editBeforeExisting  = signal<string[]>([]);
  editDuringExisting  = signal<string[]>([]);
  editAfterExisting   = signal<string[]>([]);
  isEditUploadingPhotos = signal(false);
  editPhotoProgress     = signal(0);

  editBeforePreviews = computed(() => [...this.editBeforeExisting(), ...this.editBeforeDataUrls()]);
  editDuringPreviews = computed(() => [...this.editDuringExisting(), ...this.editDuringDataUrls()]);
  editAfterPreviews  = computed(() => [...this.editAfterExisting(), ...this.editAfterDataUrls()]);

  /** Resolve the Firestore projects/{id} for a table row. */
  resolveLiveProjectId(row: any): string | null {
    if (row?.historyRow?.targetDocId) return row.historyRow.targetDocId;
    if (row?.historyRow?.afterData?.id) return row.historyRow.afterData.id;
    const candidate = row?.rawLive?.id ?? row?.id ?? null;
    if (!candidate || String(candidate).startsWith('req-')) return null;
    return String(candidate);
  }

  /** Whether the edit action should be enabled for this row. */
  isRowEditable(row: any): boolean {
    if (row?.pendingRequest) return false;
    return !!this.resolveLiveProjectId(row);
  }

  async openEditProjectRow(row: any): Promise<void> {
    const liveId = this.resolveLiveProjectId(row);
    if (!liveId) {
      this.showToast('error', 'Cannot edit this entry', ['No live project ID found. This may be a pending-create row or an incomplete history record.']);
      return;
    }
    const hasPending = this.rawPending().some(r => {
      const ids = [r.targetDocId, (r as any).createdDocId, (r as any).originalProjectId].filter(Boolean);
      return ids.includes(liveId!) && this.reqStatus(r) === 'PENDING';
    });
    if (hasPending) {
      this.showToast('error', 'Edit blocked', ['A change request is already pending for this project. Approve or reject it first.']);
      return;
    }
    let live: any = null;
    try {
      live = await this.approvalService.getTargetData('projects', liveId);
    } catch (err) {
      console.warn('[ap-edit] live project fetch failed:', err);
    }
    if (!live) {
      this.showToast('error', 'Project not found', ['This project no longer exists in the live database.']);
      return;
    }
    this.editingLiveProjectId = liveId;
    this.editingBaseline = { ...live };
    this.clearEditPhotoState();
    const schemeName = (live.scheme ?? (live as any).schemeName ?? '').trim();
    this.editExtraSchemeName.set(schemeName || null);
    this.editExtraYear.set(Number(live.yearOfCreation ?? live.sanctionedYear) || null);
    const block = (live.block ?? live.loc ?? DEFAULT_OPERATOR_LOCATION_BLOCK) as string;
    this.editSelectedBlock.set(block);
    // Load existing categorised photos
    const photos = (live.projectPhotos ?? { before: [], during: [], after: [] }) as { before: string[]; during: string[]; after: string[] };
    this.editBeforeExisting.set(photos.before ?? []);
    this.editDuringExisting.set(photos.during ?? []);
    this.editAfterExisting.set(photos.after  ?? []);
    this.editFormData.set({
      deptName:       (live.dept ?? '').trim(),
      projectName:    live.name ?? '',
      schemeName,
      block,
      gp:             live.gp ?? (live as any).gpName ?? (NAC_BLOCKS.has(block) ? 'NAC' : ''),
      costCr:         live.cost ?? null,
      spentCr:        live.spent ?? 0,
      status:         live.status ?? 'Planned',
      yearOfCreation: String(live.yearOfCreation ?? live.sanctionedYear ?? ''),
      roadLength:     live.roadLength ?? null,
      executantName:  live.executorName  ?? live.executant?.name    ?? '',
      executantContact: live.executorNumber ?? live.executant?.contact ?? '',
      editRemarks:    '',
      photoDateBefore: live.photoDates?.before ?? '',
      photoDateDuring: live.photoDates?.during ?? '',
      photoDateAfter:  live.photoDates?.after  ?? '',
    });
    this.isEditModalOpen.set(true);
  }

  closeEditModal(): void {
    this.isEditModalOpen.set(false);
    this.editingLiveProjectId = null;
    this.editingBaseline = null;
    this.editingPendingRequestId = null;
    this.pendingEditMode.set(false);
    this.editExtraSchemeName.set(null);
    this.editExtraYear.set(null);
    this.clearEditPhotoState();
  }

  async submitApproverEdit(): Promise<void> {
    const f = this.editFormData();
    if (!f.projectName?.trim()) { alert('Project name is required.'); return; }
    if (!this.editingLiveProjectId && !this.pendingEditMode()) return;
    this.isEditSubmitting.set(true);
    try {
      // Upload any newly staged photos
      const allNew = [
        ...this.editBeforeFiles().map(file => ({ cat: 'before' as const, file })),
        ...this.editDuringFiles().map(file => ({ cat: 'during' as const, file })),
        ...this.editAfterFiles().map(file =>  ({ cat: 'after'  as const, file })),
      ];
      const beforeUrls = [...this.editBeforeExisting()];
      const duringUrls = [...this.editDuringExisting()];
      const afterUrls  = [...this.editAfterExisting()];
      if (allNew.length) {
        this.isEditUploadingPhotos.set(true);
        this.editPhotoProgress.set(0);
        for (let i = 0; i < allNew.length; i++) {
          const { cat, file } = allNew[i];
          const url = await this.photoUploadService.uploadPhoto(
            `${this.editingLiveProjectId}/${cat}`, file
          );
          if (cat === 'before') beforeUrls.push(url);
          else if (cat === 'during') duringUrls.push(url);
          else afterUrls.push(url);
          this.editPhotoProgress.set(Math.round(((i + 1) / allNew.length) * 100));
        }
        this.isEditUploadingPhotos.set(false);
      }
      const updates: Record<string, any> = {
        name:           f.projectName.trim(),
        dept:           f.deptName,
        scheme:         f.schemeName.trim(),
        loc:            f.block,
        block:          f.block,
        gp:             f.gp ?? '',
        cost:           Number(f.costCr)  || 0,
        spent:          Number(f.spentCr) || 0,
        status:         f.status,
        yearOfCreation: f.yearOfCreation ?? '',
        roadLength:     f.roadLength != null && f.roadLength !== (null as any) ? Number(f.roadLength) : null,
        executant:      { name: f.executantName ?? '', contact: f.executantContact ?? '' },
        executorName:   f.executantName  ?? '',
        executorNumber: f.executantContact ?? '',
        projectPhotos:  { before: beforeUrls, during: duringUrls, after: afterUrls },
        photoDates: {
          before: f.photoDateBefore || null,
          during: f.photoDateDuring || null,
          after:  f.photoDateAfter  || null,
        },
      };
      const projectName = f.projectName.trim();
      if (this.pendingEditMode() && this.editingPendingRequestId) {
        await this.approvalService.approveRequest(
          this.editingPendingRequestId,
          f.editRemarks?.trim() || 'Approved with edits by approver',
          updates
        );
        this.closeEditModal();
        this.showToast('success', 'Project approved with changes', [`"${projectName}" has been edited and approved.`]);
      } else {
        await this.projectService.updateProject(this.editingLiveProjectId!, updates);
        this.closeEditModal();
        this.showToast('success', 'Project updated', [`"${projectName}" has been saved.`]);
      }
    } catch (err: any) {
      console.error('[ap-edit] submit failed:', err);
      this.showToast('error', 'Edit submission failed', [err?.message ?? 'Please try again.']);
    } finally {
      this.isEditSubmitting.set(false);
      this.isEditUploadingPhotos.set(false);
    }
  }

  onEditBlockChange(block: string): void {
    this.editSelectedBlock.set(block);
    const gp = NAC_BLOCKS.has(block) ? 'NAC' : '';
    this.patchEditForm({ block, gp });
  }

  onEditPhotoSelected(category: 'before' | 'during' | 'after', event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    const valid = files.filter(f => allowed.includes(f.type) && f.size <= 5 * 1024 * 1024);
    if (valid.length < files.length) alert('Some files skipped — only JPG/PNG/WEBP under 5 MB allowed.');
    const filesSignal    = category === 'before' ? this.editBeforeFiles    : category === 'during' ? this.editDuringFiles    : this.editAfterFiles;
    const dataUrlsSignal = category === 'before' ? this.editBeforeDataUrls : category === 'during' ? this.editDuringDataUrls : this.editAfterDataUrls;
    filesSignal.update(arr => [...arr, ...valid]);
    valid.forEach(file => {
      const reader = new FileReader();
      reader.onload = e => dataUrlsSignal.update(arr => [...arr, e.target?.result as string]);
      reader.readAsDataURL(file);
    });
  }

  removeEditPhoto(category: 'before' | 'during' | 'after', index: number): void {
    const existingSignal = category === 'before' ? this.editBeforeExisting : category === 'during' ? this.editDuringExisting : this.editAfterExisting;
    const filesSignal    = category === 'before' ? this.editBeforeFiles    : category === 'during' ? this.editDuringFiles    : this.editAfterFiles;
    const dataUrlsSignal = category === 'before' ? this.editBeforeDataUrls : category === 'during' ? this.editDuringDataUrls : this.editAfterDataUrls;
    const existingCount = existingSignal().length;
    if (index < existingCount) {
      existingSignal.update(arr => arr.filter((_, i) => i !== index));
    } else {
      const si = index - existingCount;
      filesSignal.update(arr => arr.filter((_, i) => i !== si));
      dataUrlsSignal.update(arr => arr.filter((_, i) => i !== si));
    }
  }

  private clearEditPhotoState(): void {
    this.editBeforeFiles.set([]);    this.editDuringFiles.set([]);    this.editAfterFiles.set([]);
    this.editBeforeDataUrls.set([]); this.editDuringDataUrls.set([]); this.editAfterDataUrls.set([]);
    this.editBeforeExisting.set([]); this.editDuringExisting.set([]); this.editAfterExisting.set([]);
    this.isEditUploadingPhotos.set(false);
    this.editPhotoProgress.set(0);
  }

  // ── Lifecycle ────────────────────────────────────────────────────
  ngOnInit(): void {
    void this.locationMaster.ensureLoaded().then(locs => this.masterLocations.set(locs));
    this.loadUserMap();
    this.subscribeDepartmentAssignments();
    this.subscribeToApproverRequests();
    this.loadHistory();
    this.subscribeToLiveProjects();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.departmentAssignmentSub?.unsubscribe();
    this.liveProjectsSub?.unsubscribe();
    this.approverRequestsSub?.unsubscribe();
  }

  private async loadUserMap(): Promise<void> {
    try {
      const users = await this.userService.getAllUsers();
      const map = new Map<string, string>();
      users.forEach(u => map.set(u.uid, u.displayName || u.email || u.uid));
      this.userMap.set(map);
    } catch (err) {
      console.warn('[approver] Could not load user directory; UIDs will show.', err);
    }
  }

  private subscribeDepartmentAssignments(): void {
    toObservable(this.currentUser, { injector: this.injector }).pipe(
      switchMap(user => {
        this.departmentAssignmentSub?.unsubscribe();
        this.mappedDepartmentIds.set([]);
        if (!user) return of([] as any[]);

        const firebaseUid = this.authService.getFirebaseAuthUid?.() ?? null;
        const candidateUserIds = Array.from(new Set([user.uid, firebaseUid].filter(Boolean) as string[]));
        if (candidateUserIds.length === 0) return of([] as any[]);

        const assignmentsCollection = collection(this.firestore, 'departmentAssignments');
        const streams = candidateUserIds.map(candidateId => {
          const q = query(assignmentsCollection, where('userId', '==', candidateId), where('isActive', '==', true));
          return collectionData(q, { idField: 'id' }) as Observable<any[]>;
        });
        return combineLatest(streams);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: async rowsPerSource => {
        const ids = (rowsPerSource || [])
          .flat()
          .map(r => (r?.departmentId || '').toString().trim())
          .filter(Boolean);
        const normalized = await this.resolveDepartmentIdsToNames(Array.from(new Set(ids)));
        this.mappedDepartmentIds.set(normalized);
      },
      error: err => {
        console.warn('[approver] departmentAssignments stream error:', err);
        this.mappedDepartmentIds.set([]);
      }
    });
  }

  private async resolveDepartmentIdsToNames(rawIds: string[]): Promise<string[]> {
    const all = this.deptService.getDepartments();
    const byName = new Map(all.map(d => [this.normalizeDepartmentKey(d.name), d.name]));
    const byShort = new Map(all.map(d => [this.normalizeDepartmentKey(d.shortName), d.name]));
    const resolved: string[] = [];
    const unknown: string[] = [];
    for (const raw of rawIds) {
      const key = this.normalizeDepartmentKey(raw);
      const known = byName.get(key) || byShort.get(key);
      if (known) resolved.push(known);
      else unknown.push(raw);
    }
    if (unknown.length > 0) {
      const fetchedNames = await Promise.all(unknown.map(async (deptDocId) => {
        try {
          const snap = await getDoc(doc(this.firestore, 'departments', deptDocId));
          return snap.exists() ? String(snap.data()?.['name'] || '').trim() : '';
        } catch { return ''; }
      }));
      resolved.push(...fetchedNames.filter(Boolean));
    }
    return Array.from(new Set(resolved));
  }

  private subscribeToLiveProjects(): void {
    this.liveProjectsSub = this.projectService.getProjects$().subscribe({
      next: projects => this.liveProjects.set(projects),
      error: err => console.warn('[approver] projects stream error:', err),
    });
  }

  /**
   * Live stream of all queue rows routed to this approver — survives hard
   * refreshes where auth resolves after ngOnInit, and keeps the table in sync
   * immediately after approve/reject (not only while status is PENDING).
   */
  private subscribeToApproverRequests(): void {
    effect(() => {
      const user = this.currentUser();
      this.approverRequestsSub?.unsubscribe();
      this.approverRequestsSub = null;

      if (!user) {
        this.rawRequests.set([]);
        this.isLoading.set(false);
        return;
      }

      this.isLoading.set(true);
      this.approverRequestsSub = this.approvalService.getApproverRequests().subscribe({
        next: requests => {
          this.rawRequests.set(requests);
          this.isLoading.set(false);
        },
        error: err => {
          console.error('[approver] approval requests stream error:', err);
          this.rawRequests.set([]);
          this.isLoading.set(false);
        },
      });
    }, { injector: this.injector });
  }

  private loadHistory(): void {
    toObservable(this.currentUser, { injector: this.injector })
      .pipe(
        switchMap(user => user ? this.approvalService.getAuditLogs() : of([] as ApprovalHistory[])),
        takeUntil(this.destroy$),
      )
      .subscribe((logs: ApprovalHistory[]) => this.rawHistory.set(logs));
  }

  // ─── Bulk selection state ──────────────────────────────────────
  /** Set of pending-request IDs currently selected via the row checkboxes. */
  selectedRequestIds = signal<Set<string>>(new Set<string>());
  /** Per-row in-flight action set (so the spinner shows on the right row). */
  rowActionInProgress = signal<Set<string>>(new Set<string>());
  /** Whether a bulk approve / reject is in flight — disables both bulk buttons. */
  bulkActionInProgress = signal<boolean>(false);

  /** Toast state — shown briefly after Approve / Reject / bulk action. */
  toast = signal<{ kind: 'success' | 'error'; title: string; lines?: string[] } | null>(null);
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private showToast(kind: 'success' | 'error', title: string, lines?: string[]): void {
    this.toast.set({ kind, title, lines });
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.toast.set(null), 4500);
  }
  dismissToast(): void { this.toast.set(null); }

  /** Pending rows currently visible in the table (used by Select-All logic). */
  private get visiblePendingRequestIds(): string[] {
    return this.projectRows()
      .filter(r => r.pendingRequest?.id)
      .map(r => r.pendingRequest.id as string);
  }

  /** Public count of selectable pending rows — used by the Select-All checkbox `disabled` binding. */
  get visiblePendingCount(): number {
    return this.visiblePendingRequestIds.length;
  }

  toggleRowSelection(requestId: string | undefined): void {
    if (!requestId) return;
    this.selectedRequestIds.update(prev => {
      const next = new Set(prev);
      next.has(requestId) ? next.delete(requestId) : next.add(requestId);
      return next;
    });
  }

  isRowSelected(requestId: string | undefined): boolean {
    return !!requestId && this.selectedRequestIds().has(requestId);
  }

  /** All visible pending rows selected? (drives the Select-All checkbox state) */
  isAllPendingSelected = computed(() => {
    const ids = this.visiblePendingRequestIds;
    if (ids.length === 0) return false;
    const sel = this.selectedRequestIds();
    return ids.every(id => sel.has(id));
  });

  /** "Indeterminate" state for the Select-All checkbox. */
  isSomePendingSelected = computed(() => {
    const ids = this.visiblePendingRequestIds;
    const sel = this.selectedRequestIds();
    const matched = ids.filter(id => sel.has(id)).length;
    return matched > 0 && matched < ids.length;
  });

  toggleSelectAllPending(): void {
    const ids = this.visiblePendingRequestIds;
    const everySelected = this.isAllPendingSelected();
    this.selectedRequestIds.update(prev => {
      const next = new Set(prev);
      if (everySelected) ids.forEach(id => next.delete(id));
      else                ids.forEach(id => next.add(id));
      return next;
    });
  }

  selectedCount = computed(() => this.selectedRequestIds().size);
  clearSelection(): void { this.selectedRequestIds.set(new Set<string>()); }

  // ── Row actions ─────────────────────────────────────────────────
  /** View a row — picks the right modal (live project vs pending request vs history). */
  viewProjectRow(row: any): void {
    if (row?.pendingRequest) {
      this.openPendingView(row);
      return;
    }
    if (row?.historyRow) {
      this.viewHistory(row.historyRow);
      return;
    }
    // Plain live project — navigate to full project details page
    const id = this.resolveLiveProjectId(row);
    if (id) {
      this.router.navigate(['/dhamnagar-dashboard/project', id]);
      return;
    }
    // Fallback: open compare modal when no Firestore id is available
    this.isReadOnly.set(true);
    this.selectedRequest.set(null);
    this.beforeData.set(null);
    this.afterData.set(row?.rawLive ?? row);
    this.modalMeta.set({
      operator: '—',
      operation: 'view',
      date: this.toDate(row?.updatedAt) || undefined,
      status: row?.approvalStatus,
      remarks: '',
    });
    this.isCompareOpen.set(true);
  }

  /** Mark a row as in-flight (or clear) so the per-row spinner can render. */
  private markRowBusy(requestId: string, busy: boolean): void {
    this.rowActionInProgress.update(prev => {
      const next = new Set(prev);
      busy ? next.add(requestId) : next.delete(requestId);
      return next;
    });
  }

  /** Returns true if the per-row action button should show a spinner. */
  isRowBusy(requestId: string | undefined): boolean {
    return !!requestId && this.rowActionInProgress().has(requestId);
  }

  /** Human label for a toast, based on what the request actually targets. */
  private entityLabel(targetCollection?: string): string {
    switch (targetCollection) {
      case 'schemes':  return 'Scheme';
      case 'projects': return 'Project';
      default:         return 'Request';
    }
  }

  /**
   * Approve a single row directly from the table (no modal — one-click).
   * Delegates to ApprovalService.approveRequest() which runs the Firestore
   * transaction: writes the live project doc (or updates / deletes it),
   * logs history, flips the queue doc to APPROVED. All subscribers update
   * live across operator / OSD / minister / dept / HM Priority / etc.
   */
  async approveRow(row: any): Promise<void> {
    const req = row?.pendingRequest as ApprovalRequest | undefined;
    if (!req?.id) {
      this.showToast('error', 'No pending request', ['Nothing to approve on this row.']);
      return;
    }
    const label = row?.name || req.targetCollection || 'this request';
    if (!confirm(`Approve ${req.operation?.toUpperCase()} for "${label}"?`)) return;

    this.markRowBusy(req.id, true);
    try {
      await this.approvalService.approveRequest(req.id, '');
      // Selected set clears any reference to this row so the bulk bar updates correctly
      this.selectedRequestIds.update(prev => { const n = new Set(prev); n.delete(req.id!); return n; });
      this.showToast('success', `${this.entityLabel(req.targetCollection)} approved successfully`, [`"${label}" approved.`]);
    } catch (err: any) {
      console.error('[approver] approve failed:', err);
      this.showToast('error', 'Approval failed', [err?.message ?? 'See console for details.']);
    } finally {
      this.markRowBusy(req.id, false);
    }
  }

  /** Reject a single row directly from the table — prompts for remarks. */
  async rejectRow(row: any): Promise<void> {
    const req = row?.pendingRequest as ApprovalRequest | undefined;
    if (!req?.id) {
      this.showToast('error', 'No pending request', ['Nothing to reject on this row.']);
      return;
    }
    const label = row?.name || req.targetCollection || 'this request';
    const remarks = prompt(`Reject ${req.operation?.toUpperCase()} for "${label}"?\n\nAdd a remark (optional):`, '');
    if (remarks === null) return;   // user hit Cancel

    this.markRowBusy(req.id, true);
    try {
      await this.approvalService.rejectRequest(req.id, remarks || '');
      this.selectedRequestIds.update(prev => { const n = new Set(prev); n.delete(req.id!); return n; });
      this.showToast('success', `${this.entityLabel(req.targetCollection)} rejected successfully`, [`"${label}" rejected.`]);
    } catch (err: any) {
      console.error('[approver] reject failed:', err);
      this.showToast('error', 'Rejection failed', [err?.message ?? 'See console for details.']);
    } finally {
      this.markRowBusy(req.id, false);
    }
  }

  /** Bulk approve every selected pending request — runs in parallel via Promise.allSettled. */
  async bulkApprove(): Promise<void> {
    const ids = Array.from(this.selectedRequestIds());
    if (ids.length === 0) return;
    if (!confirm(`Approve ${ids.length} selected request${ids.length === 1 ? '' : 's'}?`)) return;

    this.bulkActionInProgress.set(true);
    ids.forEach(id => this.markRowBusy(id, true));

    const results = await Promise.allSettled(
      ids.map(id => this.approvalService.approveRequest(id, 'Bulk approved'))
    );

    ids.forEach(id => this.markRowBusy(id, false));
    this.bulkActionInProgress.set(false);

    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed    = results.length - succeeded;

    if (succeeded > 0) {
      this.selectedRequestIds.update(prev => {
        const next = new Set(prev);
        results.forEach((r, i) => { if (r.status === 'fulfilled') next.delete(ids[i]); });
        return next;
      });
    }

    if (failed === 0) {
      this.showToast('success', 'Bulk approval completed', [`${succeeded} request${succeeded === 1 ? '' : 's'} approved.`]);
    } else if (succeeded === 0) {
      this.showToast('error', 'Bulk approval failed', [`Could not approve any of ${ids.length} requests.`]);
    } else {
      this.showToast('error', 'Bulk approval partial', [`${succeeded} approved, ${failed} failed.`]);
    }
  }

  /** Bulk reject every selected pending request — prompts for a shared remark. */
  async bulkReject(): Promise<void> {
    const ids = Array.from(this.selectedRequestIds());
    if (ids.length === 0) return;
    const remarks = prompt(`Reject ${ids.length} selected request${ids.length === 1 ? '' : 's'}?\n\nAdd a remark (optional):`, '');
    if (remarks === null) return;

    this.bulkActionInProgress.set(true);
    ids.forEach(id => this.markRowBusy(id, true));

    const results = await Promise.allSettled(
      ids.map(id => this.approvalService.rejectRequest(id, remarks || 'Bulk rejected'))
    );

    ids.forEach(id => this.markRowBusy(id, false));
    this.bulkActionInProgress.set(false);

    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed    = results.length - succeeded;

    if (succeeded > 0) {
      this.selectedRequestIds.update(prev => {
        const next = new Set(prev);
        results.forEach((r, i) => { if (r.status === 'fulfilled') next.delete(ids[i]); });
        return next;
      });
    }

    if (failed === 0) {
      this.showToast('success', 'Bulk rejection completed', [`${succeeded} request${succeeded === 1 ? '' : 's'} rejected.`]);
    } else if (succeeded === 0) {
      this.showToast('error', 'Bulk rejection failed', [`Could not reject any of ${ids.length} requests.`]);
    } else {
      this.showToast('error', 'Bulk rejection partial', [`${succeeded} rejected, ${failed} failed.`]);
    }
  }

  // ── Compare-modal handlers (unchanged from the original dashboard) ──
  async viewDetail(request: ApprovalRequest): Promise<void> {
    this.isReadOnly.set(false);
    this.selectedRequest.set(request);
    this.afterData.set((request as any).newData ?? request.payload ?? null);
    this.modalMeta.set({
      operator: this.userMap().get(request.operatorUid) || this.shortUid(request.operatorUid),
      date: this.toDate((request as any).submittedAt ?? request.createdAt) || undefined,
      status: request.status,
      remarks: request.remarks,
      operation: request.operation,
    });
    this.isLoading.set(true);
    try {
      if (request.targetDocId) {
        const data = await this.approvalService.getTargetData(request.targetCollection, request.targetDocId);
        this.beforeData.set(data ?? (request as any).oldData ?? null);
      } else {
        this.beforeData.set((request as any).oldData ?? null);
      }
      this.isCompareOpen.set(true);
    } catch (err) {
      console.error('Failed to fetch comparison data:', err);
      this.beforeData.set((request as any).oldData ?? null);
      this.isCompareOpen.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  viewHistory(row: any): void {
    const log = row as ApprovalHistory;
    this.isReadOnly.set(true);
    this.selectedRequest.set(null);
    this.beforeData.set(log.beforeData ?? null);
    this.afterData.set(log.afterData ?? null);
    this.modalMeta.set({
      operator: this.userMap().get(log.operatorUid) || this.shortUid(log.operatorUid),
      approver: log.approverUid && log.approverUid !== 'SYSTEM'
                ? (this.userMap().get(log.approverUid) || this.shortUid(log.approverUid))
                : 'System',
      date:    this.toDate(log.timestamp) || undefined,
      status:  log.status,
      remarks: log.remarks,
      operation: log.operation,
    });
    this.isCompareOpen.set(true);
  }

  async approve(
    request: ApprovalRequest | null = this.selectedRequest(),
    payload: string | { remarks: string; editedPayload?: Record<string, any> } = ''
  ): Promise<void> {
    if (!request) return;
    const remarks = typeof payload === 'string' ? payload : (payload?.remarks ?? '');
    const edits   = typeof payload === 'string' ? null   : (payload?.editedPayload ?? null);
    const label   = ((request as any).newData?.name ?? request.payload?.name) ?? request.targetCollection;
    this.isSubmittingAction.set(true);
    try {
      await this.approvalService.approveRequest(request.id!, remarks, edits);
      this.isCompareOpen.set(false);
      this.showToast('success', 'Project approved successfully', label ? [`"${label}" approved.`] : undefined);
    } catch (err: any) {
      console.error('Approval failed:', err);
      this.showToast('error', 'Approval failed', [err?.message ?? 'See console for details.']);
    } finally {
      this.isSubmittingAction.set(false);
    }
  }

  async reject(request: ApprovalRequest | null = this.selectedRequest(), remarks: string = ''): Promise<void> {
    if (!request) return;
    if (!remarks && !confirm('Reject without remarks?')) return;
    const label = ((request as any).newData?.name ?? request.payload?.name) ?? request.targetCollection;
    this.isSubmittingAction.set(true);
    try {
      await this.approvalService.rejectRequest(request.id!, remarks);
      this.isCompareOpen.set(false);
      this.showToast('success', 'Project rejected successfully', label ? [`"${label}" rejected.`] : undefined);
    } catch (err: any) {
      console.error('Rejection failed:', err);
      this.showToast('error', 'Rejection failed', [err?.message ?? 'See console for details.']);
    } finally {
      this.isSubmittingAction.set(false);
    }
  }

  // ── Pending Project View Modal methods ───────────────────────────────────

  openPendingView(row: any): void {
    const req = row.pendingRequest as ApprovalRequest;
    const data = (req as any).newData ?? req.payload ?? {};
    this.statModalFilter.set(null);
    this.pendingViewData.set(data);
    this.pendingViewRequest.set(req);
    this.pendingViewRow.set(row);
    this.isPendingViewOpen.set(true);
    this.isRejectDialogOpen.set(false);
    this.rejectDialogRemarks.set('');
    this.pendingViewLightbox.set(null);
  }

  closePendingView(): void {
    this.isPendingViewOpen.set(false);
    this.pendingViewData.set(null);
    this.pendingViewRequest.set(null);
    this.pendingViewRow.set(null);
    this.isRejectDialogOpen.set(false);
    this.rejectDialogRemarks.set('');
    this.pendingViewLightbox.set(null);
  }

  openPendingViewLightbox(src: string): void { this.pendingViewLightbox.set(src); }
  closePendingViewLightbox(): void { this.pendingViewLightbox.set(null); }

  async approveFromView(): Promise<void> {
    const req = this.pendingViewRequest();
    const row = this.pendingViewRow();
    if (!req?.id) return;
    const label = row?.name || (req as any).newData?.name || req.payload?.name || 'this project';
    this.isSubmittingFromView.set(true);
    try {
      await this.approvalService.approveRequest(req.id, '');
      this.selectedRequestIds.update(prev => { const n = new Set(prev); n.delete(req.id!); return n; });
      this.closePendingView();
      this.showToast('success', 'Project approved successfully', [`"${label}" has been approved.`]);
    } catch (err: any) {
      this.showToast('error', 'Approval failed', [err?.message ?? 'See console for details.']);
    } finally {
      this.isSubmittingFromView.set(false);
    }
  }

  openRejectDialog(): void { this.isRejectDialogOpen.set(true); }
  cancelRejectDialog(): void { this.isRejectDialogOpen.set(false); this.rejectDialogRemarks.set(''); }

  async confirmRejectFromView(): Promise<void> {
    const req = this.pendingViewRequest();
    const row = this.pendingViewRow();
    if (!req?.id) return;
    const label = row?.name || (req as any).newData?.name || req.payload?.name || 'this project';
    this.isSubmittingFromView.set(true);
    try {
      await this.approvalService.rejectRequest(req.id, this.rejectDialogRemarks());
      this.selectedRequestIds.update(prev => { const n = new Set(prev); n.delete(req.id!); return n; });
      this.closePendingView();
      this.showToast('success', 'Project rejected', [`"${label}" has been rejected.`]);
    } catch (err: any) {
      this.showToast('error', 'Rejection failed', [err?.message ?? 'See console for details.']);
    } finally {
      this.isSubmittingFromView.set(false);
    }
  }

  editFromPendingView(): void {
    const req = this.pendingViewRequest();
    const pendingData = this.pendingViewData();
    if (!req?.id || !pendingData) return;

    const schemeName = (pendingData['scheme'] ?? '').trim();
    this.editExtraSchemeName.set(schemeName || null);
    this.editExtraYear.set(Number(pendingData['yearOfCreation'] ?? pendingData['sanctionedYear']) || null);
    const block = (pendingData['block'] ?? pendingData['loc'] ?? DEFAULT_OPERATOR_LOCATION_BLOCK) as string;
    this.editSelectedBlock.set(block);

    this.editingPendingRequestId = req.id;
    this.editingLiveProjectId = req.targetDocId ?? null;
    this.editingBaseline = null;
    this.pendingEditMode.set(true);
    this.clearEditPhotoState();

    const photos = (pendingData['projectPhotos'] ?? { before: [], during: [], after: [] }) as { before: string[]; during: string[]; after: string[] };
    this.editBeforeExisting.set(photos.before ?? []);
    this.editDuringExisting.set(photos.during ?? []);
    this.editAfterExisting.set(photos.after  ?? []);

    this.editFormData.set({
      deptName:         (pendingData['dept'] ?? '').trim(),
      projectName:      pendingData['name'] ?? '',
      schemeName,
      block,
      gp:               pendingData['gp'] ?? pendingData['gpName'] ?? (NAC_BLOCKS.has(block) ? 'NAC' : ''),
      costCr:           pendingData['cost'] ?? null,
      spentCr:          pendingData['spent'] ?? 0,
      status:           pendingData['status'] ?? 'Planned',
      yearOfCreation:   String(pendingData['yearOfCreation'] ?? pendingData['sanctionedYear'] ?? ''),
      roadLength:       pendingData['roadLength'] ?? null,
      executantName:    pendingData['executorName']   ?? (pendingData['executant'] as any)?.name    ?? '',
      executantContact: pendingData['executorNumber'] ?? (pendingData['executant'] as any)?.contact ?? '',
      editRemarks:      '',
      photoDateBefore:  (pendingData['photoDates'] as any)?.before ?? '',
      photoDateDuring:  (pendingData['photoDates'] as any)?.during ?? '',
      photoDateAfter:   (pendingData['photoDates'] as any)?.after  ?? '',
    });

    this.closePendingView();
    this.isEditModalOpen.set(true);
  }
}
